import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Collapsible, Loader } from 'ncoded-component-library';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  getArrayLength,
  getIsNodeFile,
  getIsNodeFolder,
  sortDataRoomNodes,
} from 'utils';
import FileInfoInstance from './components/FileInfoInstance';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import PlusIcon from 'icons/Plus.icon';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { FileInfoInstanceProps } from './components/FileInfoInstance/FileInfoInstance.component';
import InformationcircleIcon from 'icons/Informationcircle.icon';

import './FileInfo.styles.scss';

type FileInfoProps = {
  className?: string;
  isLoading?: boolean;
  nodes?: DataRoomNode[];
  onFilesReselect: () => void;
} & Omit<FileInfoInstanceProps, 'node'>;

const FileInfo: React.FC<FileInfoProps> = (props) => {
  const {
    className,
    isLoading,
    nodes,
    checkedFileIds,
    onFilesReselect,
    ...rest
  } = props;

  const classes = classNames('shareable-file-info', className);

  const [collapse, collapseRef] = useCallbackRef<CollapsibleRef>();

  const { t } = useTranslation();

  const sortedNodes = useMemo(() => sortDataRoomNodes(nodes), [nodes]);

  const selectedLabel = useMemo(() => {
    if (nodes.every(getIsNodeFile)) return 'viewFiles';

    if (nodes.every(getIsNodeFolder)) return 'viewFolders';

    return 'viewItems';
  }, [nodes]);

  const foldersWithNewFiles = useMemo(
    () =>
      getArrayLength(
        nodes.filter(({ childrenIds }) =>
          childrenIds.some((id) => !checkedFileIds.includes(id)),
        ),
      ),
    [checkedFileIds, nodes],
  );

  return (
    <Collapsible
      ref={collapseRef}
      className={classes}
      trigger={
        <p className={classNames('trigger', { open: collapse?.open })}>
          {t(selectedLabel, { count: nodes.length })} <ChevrondownIcon />
        </p>
      }
    >
      <>
        {!!foldersWithNewFiles && (
          <div className="new-files-banner">
            <InformationcircleIcon />
            <div>
              <p>{t('foldersHaveNewFiles', { count: foldersWithNewFiles })}</p>
              <p>{t('chooseToShareNewFiles')}</p>
            </div>
          </div>
        )}
        <div className="shareable-file-info__content">
          {isLoading && <Loader />}
          <ul>
            {sortedNodes.map((node) => (
              <FileInfoInstance
                key={node?._id}
                node={node}
                checkedFileIds={checkedFileIds}
                {...rest}
              />
            ))}
          </ul>
        </div>
        <Button variant="link" icon={PlusIcon} onClick={onFilesReselect}>
          {t('addMoreFiles')}
        </Button>
      </>
    </Collapsible>
  );
};

export default FileInfo;
